import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Cookie } from 'lucide-react';
import {
  acceptAll,
  rejectAll,
  setConsent,
  needsConsentDecision,
  getConsent,
  onConsentChange,
} from '../utils/cookieConsent';
import { initAirbridge, identifyAirbridgeUser } from '../utils/airbridge';

// Routes where the banner should NOT cover content — primarily the Privacy
// Policy itself (so the user can read it before deciding) and any route
// with a fixed bottom composer/CTA the banner would otherwise block.
const HIDDEN_ROUTES = [
  '/privacy',
  '/terms',
  '/guest/chat',
  '/host/chat',
  '/venue/chat',
  '/staff-admin/chat',
  '/staff-app/chat',
  '/admin/chat',
  // Fresh-signup onboarding pages — the sticky consent banner covers
  // the role cards / continue buttons on small (360x640) Android
  // viewports, so a brand-new user physically can't tap "Host" or
  // "Venue" (testing_agent iter 303). Route into /register from here
  // where the banner reappears above the standard-height form.
  '/select-role',
  '/guest-info',
  '/host-intro-1',
  '/host-intro-2',
  '/venue/intro',
];

function shouldHideOnRoute(pathname) {
  return HIDDEN_ROUTES.some((p) => pathname === p || pathname.startsWith(`${p}/`));
}

export const CookieConsentBanner = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [visible, setVisible] = useState(() => needsConsentDecision());
  const [customizing, setCustomizing] = useState(false);
  const [analyticsChecked, setAnalyticsChecked] = useState(
    () => getConsent().categories.analytics,
  );

  // Re-evaluate whenever consent storage changes (e.g. user opened the
  // banner again from Privacy settings).
  useEffect(() => {
    const off = onConsentChange(() => {
      const needs = needsConsentDecision();
      setVisible(needs);
      if (needs) setCustomizing(false);
      setAnalyticsChecked(getConsent().categories.analytics);
    });
    return off;
  }, []);

  const finalize = useCallback(() => {
    // After consent is granted, lazily bring Airbridge online and
    // re-identify the currently logged-in user (if any) so subsequent
    // events stay attributed.
    initAirbridge();
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        identifyAirbridgeUser(JSON.parse(storedUser));
      }
    } catch {
      /* noop */
    }
    setVisible(false);
    setCustomizing(false);
  }, []);

  const handleAcceptAll = useCallback(() => {
    acceptAll();
    finalize();
  }, [finalize]);

  const handleRejectAll = useCallback(() => {
    rejectAll();
    setVisible(false);
    setCustomizing(false);
  }, []);

  const handleSavePreferences = useCallback(() => {
    setConsent({ analytics: analyticsChecked });
    if (analyticsChecked) finalize();
    else {
      setVisible(false);
      setCustomizing(false);
    }
  }, [analyticsChecked, finalize]);

  const handleOpenPrivacy = useCallback(() => {
    navigate('/privacy');
  }, [navigate]);

  const hidden = useMemo(() => shouldHideOnRoute(location.pathname), [location.pathname]);
  if (!visible || hidden) return null;

  return (
    <div
      data-testid="cookie-consent-banner"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      aria-describedby="cookie-consent-desc"
      style={{
        position: 'fixed',
        left: 12,
        right: 12,
        bottom: 'max(12px, env(safe-area-inset-bottom))',
        zIndex: 10500,
        maxWidth: 560,
        margin: '0 auto',
        background: '#0F1320',
        color: '#E6EAF2',
        borderRadius: 18,
        padding: '18px 18px 14px',
        border: '1px solid rgba(255,255,255,0.10)',
        boxShadow: '0 18px 50px rgba(0,0,0,0.45), 0 2px 10px rgba(0,0,0,0.30)',
        fontFamily: "'Manrope', system-ui, sans-serif",
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div
          style={{
            width: 36,
            height: 36,
            borderRadius: 12,
            background: 'rgba(255,184,108,0.14)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Cookie size={18} color="#FFB86C" strokeWidth={2.2} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            id="cookie-consent-title"
            style={{ fontSize: 14.5, fontWeight: 700, marginBottom: 4, letterSpacing: 0.1 }}
          >
            We use cookies
          </div>
          <div
            id="cookie-consent-desc"
            style={{ fontSize: 12.5, lineHeight: 1.45, color: '#B7BFCC' }}
          >
            We use essential cookies to keep you signed in. With your permission,
            we also use analytics cookies (Airbridge) to understand how invites
            and shares spread, so we can improve the product.{' '}
            <button
              type="button"
              onClick={handleOpenPrivacy}
              data-testid="cookie-consent-privacy-link"
              style={{
                background: 'transparent',
                border: 'none',
                color: '#9FB6FF',
                textDecoration: 'underline',
                padding: 0,
                cursor: 'pointer',
                fontSize: 12.5,
                fontFamily: 'inherit',
              }}
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>

      {customizing ? (
        <div
          data-testid="cookie-consent-customize-panel"
          style={{
            marginTop: 14,
            padding: '12px 12px 4px',
            borderRadius: 12,
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.06)',
          }}
        >
          <CategoryRow
            label="Strictly necessary"
            description="Required for sign-in, sessions and security. Always on."
            checked
            disabled
            testId="cookie-consent-category-necessary"
          />
          <CategoryRow
            label="Analytics & attribution"
            description="Airbridge — measures how invite links are shared and converted."
            checked={analyticsChecked}
            onChange={() => setAnalyticsChecked((v) => !v)}
            testId="cookie-consent-category-analytics"
          />
        </div>
      ) : null}

      <div
        style={{
          marginTop: 14,
          display: 'flex',
          gap: 8,
          flexWrap: 'wrap',
          justifyContent: 'flex-end',
        }}
      >
        {!customizing ? (
          <button
            type="button"
            onClick={() => setCustomizing(true)}
            data-testid="cookie-consent-customize-button"
            style={pillButtonStyle({ tone: 'ghost' })}
          >
            Customize
          </button>
        ) : null}
        <button
          type="button"
          onClick={handleRejectAll}
          data-testid="cookie-consent-reject-button"
          style={pillButtonStyle({ tone: 'ghost' })}
        >
          Reject all
        </button>
        {customizing ? (
          <button
            type="button"
            onClick={handleSavePreferences}
            data-testid="cookie-consent-save-button"
            style={pillButtonStyle({ tone: 'primary' })}
          >
            Save preferences
          </button>
        ) : (
          <button
            type="button"
            onClick={handleAcceptAll}
            data-testid="cookie-consent-accept-button"
            style={pillButtonStyle({ tone: 'primary' })}
          >
            Accept all
          </button>
        )}
      </div>
    </div>
  );
};

function pillButtonStyle({ tone }) {
  const base = {
    borderRadius: 999,
    padding: '9px 16px',
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: 0.2,
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'background 120ms ease, transform 120ms ease',
  };
  if (tone === 'primary') {
    return {
      ...base,
      background: '#FFFFFF',
      color: '#0F1320',
      border: '1px solid rgba(255,255,255,0.9)',
    };
  }
  return {
    ...base,
    background: 'rgba(255,255,255,0.06)',
    color: '#E6EAF2',
    border: '1px solid rgba(255,255,255,0.14)',
  };
}

function CategoryRow({ label, description, checked, onChange, disabled, testId }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 12,
        padding: '8px 0',
        borderBottom: '1px solid rgba(255,255,255,0.05)',
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: '#E6EAF2' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2, lineHeight: 1.4 }}>
          {description}
        </div>
      </div>
      <Toggle checked={checked} disabled={disabled} onChange={onChange} testId={testId} />
    </div>
  );
}

function Toggle({ checked, disabled, onChange, testId }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onChange}
      data-testid={testId}
      aria-pressed={checked}
      aria-disabled={disabled || undefined}
      style={{
        width: 38,
        height: 22,
        borderRadius: 999,
        border: 'none',
        background: checked ? '#5B8DEF' : 'rgba(255,255,255,0.14)',
        position: 'relative',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.7 : 1,
        padding: 0,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: 'absolute',
          top: 2,
          left: checked ? 18 : 2,
          width: 18,
          height: 18,
          borderRadius: '50%',
          background: '#FFFFFF',
          transition: 'left 140ms ease',
          boxShadow: '0 1px 3px rgba(0,0,0,0.30)',
        }}
      />
    </button>
  );
}

export default CookieConsentBanner;
